import { doc, runTransaction, type DocumentData, type Firestore } from 'firebase/firestore';
import { COUNTERS, formatRecordId, type CounterPrefix } from '@shared/numbering';

const MAX_SKIPS = 50;

/**
 * Atomically allocates the next human-readable id for `prefix` and creates
 * `collectionName/{id}` with `build(id)` in the same transaction. Ids that
 * already exist (for example seeded sample records) are skipped so a stale
 * counter can never overwrite a record.
 */
export async function createNumberedDoc(
  db: Firestore,
  prefix: CounterPrefix,
  collectionName: string,
  build: (id: string) => DocumentData,
): Promise<string> {
  const spec = COUNTERS[prefix];
  return runTransaction(db, async (tx) => {
    const counterRef = doc(db, 'counters', prefix);
    const snap = await tx.get(counterRef);
    let next = snap.exists() ? (snap.data().next as number) : spec.start;
    let id = formatRecordId(prefix, next);
    let target = doc(db, collectionName, id);
    for (let i = 0; i < MAX_SKIPS && (await tx.get(target)).exists(); i++) {
      next += 1;
      id = formatRecordId(prefix, next);
      target = doc(db, collectionName, id);
    }
    tx.set(counterRef, { next: next + 1, width: spec.width, separator: spec.separator }, { merge: true });
    tx.set(target, build(id));
    return id;
  });
}
